import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, Plus, ArrowRight, Trash } from "@phosphor-icons/react";
import { SALON_IMAGES } from "@/lib/assets";
import { toast } from "sonner";

const statusLabel = { verarbeitung: "In Arbeit", fertig: "Fertig", fehler: "Fehlgeschlagen" };

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async (q = "") => {
    try {
      const { data } = await api.get("/analyses", { params: { q } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const remove = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    await api.delete(`/analyses/${id}`);
    toast.success("Analyse entfernt");
    load(query);
  };

  const fertig = items.filter((i) => i.status === "fertig").length;

  return (
    <div className="mx-auto max-w-[1400px] px-8 lg:px-12 py-16 lg:py-24" data-testid="dashboard-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-20 rise">
        <div>
          <p className="label-xs mb-5">Salon Figaro Busch</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.02]">
            Stilberatungen
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-xl leading-relaxed">
            {fertig} abgeschlossene {fertig === 1 ? "Beratung" : "Beratungen"} im Archiv. Starte eine neue Analyse
            direkt am Platz.
          </p>
        </div>
        <Button
          data-testid="start-new-analysis-button"
          onClick={() => navigate("/neue-analyse")}
          className="h-14 px-10 rounded-sm bg-foreground text-primary-foreground text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-foreground/90 hover:-translate-y-[1px] transition-transform duration-200"
        >
          <Plus size={16} className="mr-3" /> Neue Analyse
        </Button>
      </div>

      <div className="flex items-center gap-4 border-b border-border pb-4 mb-12 max-w-md">
        <MagnifyingGlass size={18} className="text-muted-foreground" />
        <Input
          data-testid="archive-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nach Kundenname suchen"
          className="border-0 bg-transparent px-0 h-8 shadow-none focus-visible:ring-0 placeholder:text-muted-foreground"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade Archiv …</p>
      ) : items.length === 0 ? (
        <div
          className="border border-border bg-card rounded-sm overflow-hidden grid md:grid-cols-2"
          data-testid="archive-empty"
        >
          <div className="p-16 flex flex-col justify-center">
            <h3 className="font-display text-2xl font-light mb-3">Noch keine Beratung im Archiv</h3>
            <p className="text-sm text-muted-foreground">
              Lade sieben Aufnahmen hoch und erstelle den ersten Style-Report.
            </p>
          </div>
          <div className="hidden md:block relative min-h-[280px]">
            <img
              src={SALON_IMAGES.farbkarten}
              alt="Farbkarten im Salon Figaro Busch"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="archive-grid">
          {items.map((item, i) => (
            <Link
              key={item.id}
              to={`/analyse/${item.id}`}
              data-testid={`archive-card-${item.id}`}
              className="group border border-border bg-card rounded-sm overflow-hidden hover:-translate-y-[2px] hover:border-foreground/30 transition-[transform,border-color] duration-300 rise"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="aspect-[4/3] bg-secondary overflow-hidden">
                {item.photo_paths?.[0] ? (
                  <img
                    src={imageUrl(item.photo_paths[0])}
                    alt={item.customer_name}
                    className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                ) : null}
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-medium tracking-tight">{item.customer_name}</h3>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {new Date(item.created_at).toLocaleDateString("de-AT", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                      {" · "}
                      {item.gender === "damen" ? "Damen" : "Herren"}
                    </p>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1 rounded-sm ${
                      item.status === "fertig"
                        ? "bg-accent text-accent-foreground"
                        : item.status === "fehler"
                        ? "bg-destructive/10 text-destructive"
                        : "bg-secondary text-muted-foreground softpulse"
                    }`}
                  >
                    {statusLabel[item.status]}
                  </span>
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2">
                    Report öffnen <ArrowRight size={14} />
                  </span>
                  <button
                    data-testid={`archive-delete-${item.id}`}
                    onClick={(e) => remove(item.id, e)}
                    className="text-muted-foreground hover:text-destructive transition-colors duration-200"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
